import { useState } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { openBillingPortal } from "@/lib/billing";
import { tierDisplayName, type Tier } from "@/lib/tier";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const STORAGE_KEY = "trial-banner-dismissed-at";

/**
 * Top-of-app banner shown only while the user is in a Stripe trial.
 * Dismissible per-day so we don't pester them constantly, but reappears
 * the next day so they can't miss the trial-ending date entirely.
 */
export function TrialBanner() {
  const { isTrialing, trialDaysRemaining, trialEndsAt } = useSubscription();
  const { active } = useWorkspace();
  const [hidden, setHidden] = useState<boolean>(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (!dismissed) return false;
      // Re-show on a new calendar day.
      return new Date(dismissed).toDateString() === new Date().toDateString();
    } catch {
      return false;
    }
  });

  if (!isTrialing || trialDaysRemaining === null || hidden) return null;

  const tierLabel = tierDisplayName((active?.account_tier ?? null) as Tier | null);
  const dateLabel = trialEndsAt
    ? trialEndsAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;
  const daysLabel =
    trialDaysRemaining === 0
      ? "today"
      : trialDaysRemaining === 1
      ? "in 1 day"
      : `in ${trialDaysRemaining} days`;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* noop */ }
    setHidden(true);
  };

  return (
    <div className="w-full bg-blue-100 border-b border-blue-300 px-4 py-2 text-center text-xs text-blue-900 flex items-center justify-center gap-3">
      <span>
        Your <strong>{tierLabel}</strong> trial ends {daysLabel}
        {dateLabel ? ` (${dateLabel})` : ""}. Your card will be charged then.
      </span>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs text-blue-900 underline"
        onClick={() => void openBillingPortal()}
      >
        Manage billing
      </Button>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="ml-2 rounded p-0.5 hover:bg-blue-200"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
